import { ACTIONS, ORG_SEGMENTS, RESOURCES } from './model';
const ALL_DEALS = {
    read: 'org',
    create: 'org',
    update: 'org',
    delete: 'org',
    assign: 'org'
};
// Managing the team: invite, change a role or status, remove, assign properties.
const TEAM = {
    read: 'org',
    create: 'org',
    update: 'org',
    delete: 'org'
};
const ALL_PROPERTIES = {
    read: 'org',
    create: 'org',
    update: 'org',
    delete: 'org',
    assign: 'org'
};
const ALL_RENTALS = {
    read: 'org',
    create: 'org',
    update: 'org',
    delete: 'org'
};
// Every member sees the organization they work in; the owner and admins
// change its settings, such as the segment.
const ORG_READ = { read: 'org' };
// Finances: invoices and the payments recorded against them.
const FINANCES = {
    read: 'org',
    create: 'org',
    update: 'org',
    delete: 'org'
};
const FINANCES_READ = { read: 'org' };
const ALL_DOCUMENTS = {
    read: 'org',
    create: 'org',
    update: 'org',
    delete: 'org'
};
const ALL_CONTACTS = {
    read: 'org',
    create: 'org',
    update: 'org',
    delete: 'org',
    assign: 'org'
};
const ORG_SETTINGS = { read: 'org', update: 'org' };
// Listings follow properties: whoever works with every property of the
// organization works with every listing.
const ALL_LISTINGS = {
    read: 'org',
    create: 'org',
    update: 'org',
    delete: 'org'
};
const ALL_TASKS = {
    read: 'org',
    create: 'org',
    update: 'org',
    delete: 'org',
    assign: 'org'
};
// A member's own tasks: those they created, and those set for them to do,
// which they read and mark done but neither delete nor hand on.
const OWN_TASKS = {
    read: ['own', 'responsible'],
    create: 'own',
    update: ['own', 'responsible'],
    delete: 'own',
    assign: 'own'
};
/**
 * Fields whose change hands a record to someone else. Changing them needs
 * `assign`; a role with `update` but without `assign` cannot touch them.
 */
export const ASSIGN_FIELDS = {
    Deal: ['managerId'],
    Account: [],
    Organization: [],
    Property: ['ownerId', 'managerId'],
    Rental: [],
    Invoice: [],
    // The account a payment without an invoice belongs to.
    Transaction: ['ownerId'],
    Contact: ['ownerId'],
    Document: [],
    // Who the task is set for.
    Task: ['responsibleId'],
    ExternalListing: [],
    BuildProfile: [],
    RentalAutomation: [],
    StudioChat: []
};
/**
 * Fields a scope's grant may write, where it may not write them all: the
 * member a task is set for marks it done, while its text, due date, priority
 * and binding stay with the creator (the default of most task trackers).
 * Rules built from such a grant carry the fields, so the policy client and
 * the web judge a write by what it changes.
 */
export const SCOPE_FIELDS = {
    Task: { responsible: { update: ['isDone'] } }
};
/**
 * Relations whose rows grant access to a record, with the field naming the
 * account each row grants it to: a property's assignments to agents.
 * Writing them hands the record out, which needs `assign` and accounts of
 * the organization, like changing an assign field.
 */
export const ASSIGN_RELATIONS = {
    Property: { subAccountAssignments: 'subAccountId' }
};
// The property fields the Rental rules read (SCOPE_CONDITIONS.Rental).
const PROPERTY_RULE_FIELDS = {
    ownerId: true,
    deletedAt: true,
    managerId: true,
    subAccountAssignments: { select: { subAccountId: true } }
};
/**
 * Relations a resource's rules read, per resource: a rental's rules go
 * through its property. A new row carries only the foreign key, so before a
 * create (or a write that moves the row to another related row) is checked,
 * the policy client loads each one with the fields its rules name.
 */
export const RULE_RELATIONS = {
    Rental: {
        property: {
            model: 'property',
            foreignKey: 'propertyId',
            select: PROPERTY_RULE_FIELDS
        }
    },
    Invoice: {
        rental: {
            model: 'rental',
            foreignKey: 'rentalId',
            select: { property: { select: PROPERTY_RULE_FIELDS } }
        }
    },
    // An automation's rules are its rental's (SCOPE_CONDITIONS.RentalAutomation).
    RentalAutomation: {
        rental: {
            model: 'rental',
            foreignKey: 'rentalId',
            select: {
                deletedAt: true,
                property: { select: PROPERTY_RULE_FIELDS }
            }
        }
    },
    // A listing's rules are its property's (SCOPE_CONDITIONS.ExternalListing).
    ExternalListing: {
        property: {
            model: 'property',
            foreignKey: 'propertyId',
            select: PROPERTY_RULE_FIELDS
        }
    },
    Transaction: {
        invoice: {
            model: 'invoice',
            foreignKey: 'invoiceId',
            select: {
                rental: {
                    select: { property: { select: PROPERTY_RULE_FIELDS } }
                }
            }
        }
    }
};
/**
 * What a role's grants may contain, per resource: the actions that apply to
 * it and the scopes the services can filter it by. A scope is a data filter
 * (see SCOPE_CONDITIONS), so a new one needs code; a role that combines the
 * existing ones does not.
 *
 * The team (`Account`) is managed as a whole: auth-service asks only whether
 * the caller may act on the team at all, so a narrower scope would be
 * enforced as the whole team. Offer it only once team operations filter by it.
 */
export const VOCABULARY = {
    Deal: { actions: ACTIONS, scopes: ['own', 'org', 'rented'] },
    Account: {
        actions: ['read', 'create', 'update', 'delete'],
        scopes: ['org']
    },
    Organization: { actions: ['read', 'update'], scopes: ['org'] },
    Property: { actions: ACTIONS, scopes: ['own', 'org', 'deal', 'rented'] },
    Rental: {
        actions: ['read', 'create', 'update', 'delete'],
        scopes: ['own', 'org']
    },
    Invoice: {
        actions: ['read', 'create', 'update', 'delete'],
        scopes: ['own', 'org']
    },
    Transaction: {
        actions: ['read', 'create', 'update', 'delete'],
        scopes: ['own', 'org']
    },
    Contact: { actions: ACTIONS, scopes: ['own', 'org', 'rented'] },
    Document: {
        actions: ['read', 'create', 'update', 'delete'],
        scopes: ['own', 'org']
    },
    Task: { actions: ACTIONS, scopes: ['own', 'org', 'responsible'] },
    ExternalListing: {
        actions: ['read', 'create', 'update', 'delete'],
        scopes: ['own', 'org', 'deal', 'rented']
    },
    BuildProfile: {
        actions: ['read', 'create', 'update', 'delete'],
        scopes: ['own', 'org']
    },
    RentalAutomation: {
        actions: ['read', 'create', 'update', 'delete'],
        scopes: ['own', 'org']
    },
    // One switch, using the studio at all: a project stays its author's (it
    // is their v0 chat), and a published site is taken down through its SITE
    // listing.
    StudioChat: {
        actions: ['create'],
        scopes: ['own', 'org']
    }
};
/**
 * Every action the vocabulary has, over the whole organization: what the
 * owner, a system role, holds. Built from the vocabulary rather than stored,
 * so a resource added to it reaches the owner without a migration.
 */
export const ORGANIZATION_WIDE = Object.fromEntries(RESOURCES.map(resource => [
    resource,
    Object.fromEntries(VOCABULARY[resource].actions.map(action => [action, 'org']))
]));
/**
 * The grants the built-in roles start with. The migration seeds them into
 * `role_grants` (an integration test keeps the two equal); from then on the
 * database is the source, and the platform's super admin edits it
 * (ADR 0003). The owner is not stored: it holds ORGANIZATION_WIDE.
 *
 * The owner and admins run the team. A sales head works with every deal and
 * sees the team, so they can hand deals to agents; an agent works only with
 * the deals they manage, and cannot hand them over or delete them.
 */
export const DEFAULT_GRANTS = {
    OWNER: ORGANIZATION_WIDE,
    ADMIN: {
        Deal: ALL_DEALS,
        Account: TEAM,
        Organization: ORG_SETTINGS,
        Property: ALL_PROPERTIES,
        Rental: ALL_RENTALS,
        Invoice: FINANCES,
        Transaction: FINANCES,
        Contact: ALL_CONTACTS,
        Document: ALL_DOCUMENTS,
        Task: ALL_TASKS,
        ExternalListing: ALL_LISTINGS,
        // The site, the apps and automation are the owner's and the admins'
        // (the roles matrix): no other seeded role holds them.
        BuildProfile: {
            read: 'org',
            create: 'org',
            update: 'org',
            delete: 'org'
        },
        RentalAutomation: {
            read: 'org',
            create: 'org',
            update: 'org',
            delete: 'org'
        },
        StudioChat: {
            create: 'org'
        }
    },
    MANAGER: {
        Deal: ALL_DEALS,
        Organization: ORG_READ,
        Property: ALL_PROPERTIES,
        Rental: ALL_RENTALS,
        Invoice: FINANCES_READ,
        Transaction: FINANCES_READ,
        Contact: ALL_CONTACTS,
        Document: ALL_DOCUMENTS,
        Task: ALL_TASKS,
        ExternalListing: ALL_LISTINGS
    },
    // The roles matrix: finances in full, rentals
    // read-only, no properties section and no deals. What a rental's screen
    // shows stays readable: the property under it, and the deal it came from
    // with its conversation and contract.
    ACCOUNTANT: {
        Deal: { read: 'rented' },
        Organization: ORG_READ,
        Property: { read: 'rented' },
        Rental: { read: 'org' },
        Invoice: FINANCES,
        Transaction: FINANCES,
        // The tenants of the rentals they bill.
        Contact: { read: 'rented' },
        // Documents of what they read, read-only.
        Document: { read: 'own' },
        Task: OWN_TASKS
    },
    SALES_HEAD: {
        Deal: ALL_DEALS,
        Account: { read: 'org' },
        Organization: ORG_READ,
        Property: ALL_PROPERTIES,
        Rental: ALL_RENTALS,
        Invoice: FINANCES_READ,
        Transaction: FINANCES_READ,
        Contact: ALL_CONTACTS,
        Document: ALL_DOCUMENTS,
        Task: ALL_TASKS,
        ExternalListing: ALL_LISTINGS
    },
    // An agent works with the properties they created, manage or were
    // assigned, and reads the property of a deal they manage; they cannot
    // hand a property over or delete it. Rentals: read-only, on their own
    // properties. Contacts: their own and those of their deals.
    AGENT: {
        Deal: { read: 'own', create: 'own', update: 'own' },
        Organization: ORG_READ,
        Property: { read: ['own', 'deal'], create: 'own', update: 'own' },
        Rental: { read: 'own' },
        // Their own contacts and deals' clients; the tenants of their rentals
        // to read.
        Contact: { read: ['own', 'rented'], create: 'own', update: 'own' },
        // Documents of what they reach.
        Document: { read: 'own', create: 'own', update: 'own' },
        Task: OWN_TASKS,
        // The listings of their own properties: publish and pause, as they
        // edit those properties; no deleting, as for properties. They see,
        // without changing, those of a deal's property they read.
        ExternalListing: {
            read: ['own', 'deal'],
            create: 'own',
            update: 'own'
        }
    }
};
/** The segments that offer each built-in role at first; also seeded. */
export const DEFAULT_ROLE_SEGMENTS = {
    OWNER: [],
    ADMIN: ORG_SEGMENTS,
    MANAGER: ORG_SEGMENTS,
    ACCOUNTANT: ORG_SEGMENTS,
    SALES_HEAD: ['AGENCY'],
    AGENT: ['AGENCY']
};
function isResource(value) {
    return RESOURCES.includes(value);
}
/**
 * Grants from stored rows. A row the vocabulary does not know is skipped, so
 * an outdated row never widens access; validateGrants keeps them out.
 */
export function grantsFromRows(rows) {
    const grants = {};
    for (const row of rows) {
        if (!isResource(row.resource))
            continue;
        const vocabulary = VOCABULARY[row.resource];
        if (!vocabulary.actions.includes(row.action))
            continue;
        const scopes = row.scopes.filter((scope) => vocabulary.scopes.includes(scope));
        if (scopes.length === 0)
            continue;
        grants[row.resource] ??= {};
        grants[row.resource][row.action] = scopes;
    }
    return grants;
}
/** Stored rows for grants, one per resource and action. */
export function rowsFromGrants(grants) {
    const rows = [];
    for (const resource of RESOURCES) {
        for (const action of ACTIONS) {
            const granted = grants[resource]?.[action];
            if (granted)
                rows.push({ resource, action, scopes: [granted].flat() });
        }
    }
    return rows;
}
/** Problems with grants a super admin submits; empty when they are valid. */
export function validateGrants(rows) {
    const problems = [];
    const seen = new Set();
    // The team screens read the roles to give from the organization, so a
    // role that adds or changes members has to be able to read it.
    const runsTeam = rows.some(row => row.resource === 'Account' &&
        (row.action === 'create' || row.action === 'update'));
    const readsOrganization = rows.some(row => row.resource === 'Organization' &&
        row.action === 'read' &&
        row.scopes.length > 0);
    if (runsTeam && !readsOrganization)
        problems.push('Managing the team needs Organization.read');
    // Payments are shown with their invoices, and recorded against them.
    const touchesPayments = rows.some(row => row.resource === 'Transaction');
    const readsInvoices = rows.some(row => row.resource === 'Invoice' &&
        row.action === 'read' &&
        row.scopes.length > 0);
    if (touchesPayments && !readsInvoices)
        problems.push('Working with payments needs Invoice.read');
    for (const row of rows) {
        const label = `${row.resource}.${row.action}`;
        if (!isResource(row.resource)) {
            problems.push(`Unknown resource ${row.resource}`);
            continue;
        }
        const vocabulary = VOCABULARY[row.resource];
        if (!vocabulary.actions.includes(row.action)) {
            problems.push(`${row.resource} has no action ${row.action}`);
        }
        if (row.scopes.length === 0)
            problems.push(`${label} names no scope`);
        for (const scope of row.scopes) {
            if (!vocabulary.scopes.includes(scope)) {
                problems.push(`${row.resource} has no scope ${scope}`);
            }
        }
        if (seen.has(label))
            problems.push(`${label} is listed twice`);
        seen.add(label);
    }
    return problems;
}
/** The scopes a grant names, as a list. */
function scopeList(granted) {
    return granted === undefined ? [] : [granted].flat();
}
/**
 * Whether `holder` already has everything `target` grants: every action of
 * `target`, over rows `holder` reaches too. `org` reaches every row of the
 * organization, so it covers the narrower scopes.
 *
 * Giving a role, or changing a member who holds one, needs this: a member
 * who runs the team must not hand out, or take from someone, more than they
 * hold (no escalation through delegation; see ADR 0003).
 */
export function grantsCover(holder, target) {
    for (const resource of RESOURCES) {
        for (const action of ACTIONS) {
            const wanted = scopeList(target[resource]?.[action]);
            if (wanted.length === 0)
                continue;
            const held = scopeList(holder[resource]?.[action]);
            if (held.includes('org'))
                continue;
            if (!wanted.every(scope => held.includes(scope)))
                return false;
        }
    }
    return true;
}
/** Whether the grants allow `action` on every row of the organization. */
export function grantsOrganizationWide(grants, resource, action) {
    return scopeList(grants[resource]?.[action]).includes('org');
}
/**
 * Scopes that reach rows only as the context of another record — a rental's
 * property, the deal it came from, its tenants — so the rental's screen can
 * show them. They do not open the resource's own section: an accountant reads
 * the property under a rental they bill, not the properties section.
 */
export const RELATED_ONLY_SCOPES = ['rented'];
/**
 * Whether a role's grants open a resource's section for an action: some
 * scope for it reaches rows in their own right, not only through another
 * record (RELATED_ONLY_SCOPES).
 */
export function grantsSection(grants, action, resource) {
    return scopeList(grants[resource]?.[action]).some(scope => !RELATED_ONLY_SCOPES.includes(scope));
}
